/**
 * LieferscheinPositionen — Review-Screen für einen Lieferschein.
 *
 * Zeigt die von Clara gelesenen Positionen (Material + Menge), lässt sie korrigieren,
 * gegen den Material-Katalog verknüpfen (oder neu anlegen) und — bei mehreren
 * vorausgewählten Projekten — pro Position einem Projekt zuweisen.
 *
 * Bewusst eigener Screen statt der Geld-Beleg-Felder: ein Lieferschein hat
 * keinen Betrag, sondern Mengen.
 *
 * Seit 03.10.2026 dieselbe Logik wie in Kontor: ein unsicherer Material-
 * Vorschlag ist als solcher markiert und wird mit „Stimmt“ bestätigt; erst
 * bestätigte Zuordnungen merkt sich der Lieferant. Positionen lassen sich
 * hinzufügen und entfernen, damit die Erfassung vollständig korrigierbar ist.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ClaraHinweis from "./ClaraHinweis";
import { ArrowLeft, Check, Loader2, Maximize2, Plus, RefreshCw, Search, X } from "lucide-react";
import {
  api,
  type Beleg,
  type LieferscheinPosition,
  type LieferscheinPositionPatch,
  type MaterialKatalog,
  istEntwurfStatus,
} from "../../lib/api";
import { useVollbildZurueck } from "../../lib/vollbild";

/** Eine Schreibweise je Einheit — dieselbe Liste wie der Server (einheiten.py). */
const EINHEITEN = ["t", "kg", "m³", "m²", "m", "lfm", "l", "Stk", "Sack", "Pck", "Palette", "Rolle"];
const OFFEN = new Set(["", "vorschlag"]);

/** Material noch nicht sicher: kein Katalog oder nur ein Vorschlag. Keine Ware zählt nicht. */
function istOffen(p: LieferscheinPosition): boolean {
  return (p.zuordnung ?? "") !== "ignoriert" && (!p.katalog_id || OFFEN.has(p.zuordnung ?? ""));
}

export default function LieferscheinPositionen({ beleg, onLesen }: { beleg: Beleg; onLesen?: () => void }) {
  const id = beleg.id;
  const nav = useNavigate();
  const qc = useQueryClient();
  const [fullscreen, setFullscreen] = useState(false);
  useVollbildZurueck(fullscreen, () => setFullscreen(false));

  const posQ = useQuery({
    queryKey: ["lieferschein-positionen", id],
    queryFn: () => api.listPositionen(id),
    enabled: !!id,
  });
  const projekteQ = useQuery({
    queryKey: ["lieferschein-projekte", id],
    queryFn: () => api.listLieferscheinProjekte(id),
    enabled: !!id,
  });
  const katalogQ = useQuery({
    queryKey: ["material-katalog"],
    queryFn: () => api.listMaterialKatalog(),
    staleTime: 60 * 60 * 1000,
  });

  // „Neu erkennen": Clara liest im Hintergrund (über die Beleg-Seite, die
  // nachfragt, bis sie fertig ist, und dann die Positionen neu holt).
  const claraLiest = !!beleg.ki_laeuft;
  const extractM = { isPending: claraLiest, mutate: () => onLesen?.() };

  // Entwurf abschließen: hebt Status entwurf→erfasst (sonst bleibt der
  // Lieferschein in der „Entwürfe"-Liste hängen). Erst danach zur Inbox.
  const finalizeM = useMutation({
    mutationFn: () => api.finalizeBeleg(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["belege-recent"] });
      nav("/inbox");
    },
  });

  const projekte = projekteQ.data ?? [];
  const mehrereProjekte = projekte.length > 1;
  const positionen = posQ.data ?? [];
  const istEntwurf = istEntwurfStatus(beleg.status);
  const projekteFehlen =
    mehrereProjekte && positionen.some((p) => !p.projekt_id);
  const offen = positionen.filter(istOffen).length;

  return (
    <div className="m-app" style={{ minHeight: "100vh", paddingBottom: 96 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "calc(env(safe-area-inset-top) + 14px) 16px 12px" }}>
        <button onClick={() => nav("/inbox")} aria-label="Zurück"
          style={{ background: "transparent", border: "none", color: "var(--m-ink-3)", padding: 4, cursor: "pointer", display: "flex" }}>
          <ArrowLeft size={22} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ fontSize: 17, fontWeight: 700, color: "var(--m-ink)", margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            🚚 {beleg.lieferant || "Lieferschein"}
          </h1>
          <div style={{ fontSize: 12, color: "var(--m-ink-3)" }}>
            {beleg.datum || "kein Datum"} ·{" "}
            {projekte.length === 0
              ? "kein Projekt"
              : projekte.length === 1
              ? projekte[0].projekt_title
              : `${projekte.length} Projekte`}
          </div>
        </div>
      </div>

      <div style={{ padding: "0 16px" }}>
        {/* PDF-Vorschau */}
        <button
          onClick={() => setFullscreen(true)}
          style={{ position: "relative", width: "100%", height: 150, borderRadius: 12, overflow: "hidden", border: "1px solid var(--m-line)", background: "var(--m-card)", cursor: "pointer", marginBottom: 16, padding: 0 }}
        >
          <img src={api.thumbnailUrl(id, 360)} alt="Lieferschein"
            style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }} />
          <span style={{ position: "absolute", right: 8, bottom: 8, background: "rgba(0,0,0,0.55)", color: "white", borderRadius: 8, padding: "4px 8px", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Maximize2 size={14} /> Ansehen
          </span>
        </button>

        {/* Positionen */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <h2 style={{ fontSize: 11, fontWeight: 700, color: "var(--m-ink-3)", textTransform: "uppercase", letterSpacing: 0.6, margin: 0 }}>
            Gelieferte Positionen ({positionen.length})
          </h2>
          <button onClick={() => extractM.mutate()} disabled={extractM.isPending}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: "1px solid var(--m-line)", borderRadius: 999, padding: "6px 12px", color: "var(--m-ink-2)", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            {extractM.isPending ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />}
            Neu erkennen
          </button>
        </div>

        {posQ.isLoading && <Loading />}
        <ClaraHinweis laeuft={claraLiest} fehler={!beleg.ki_extrahiert ? beleg.ki_fehler : ""} was="Lieferant und Positionen" onNochmal={onLesen} />
        {!posQ.isLoading && !claraLiest && positionen.length === 0 && (
          <div style={{ padding: 20, textAlign: "center", color: "var(--m-ink-3)", fontSize: 14, background: "var(--m-card)", borderRadius: 12, border: "1px dashed var(--m-line)" }}>
            Noch keine Positionen erkannt. Tippe „Neu erkennen".
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {positionen.map((p) => (
            <PositionRow
              key={p.id}
              pos={p}
              katalog={katalogQ.data ?? []}
              projekte={projekte}
              showProjekt={mehrereProjekte}
              belegId={id}
            />
          ))}
        </div>
        <PositionNeu belegId={id} />
      </div>

      {/* Footer */}
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, padding: "12px 16px calc(env(safe-area-inset-bottom) + 14px)", background: "linear-gradient(to top, var(--m-bg) 70%, transparent)" }}>
        <button
          onClick={() => (istEntwurf ? finalizeM.mutate() : nav("/inbox"))}
          disabled={finalizeM.isPending}
          style={{ width: "100%", height: 54, borderRadius: 14, border: "none", background: "var(--m-accent, #6db04a)", color: "white", fontSize: 16, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 10, cursor: finalizeM.isPending ? "not-allowed" : "pointer", opacity: finalizeM.isPending ? 0.6 : 1 }}>
          {finalizeM.isPending ? <Loader2 size={20} className="spin" /> : <Check size={20} />}
          {finalizeM.isPending ? "Schließe ab…" : istEntwurf ? "Abschließen" : "Fertig"}
        </button>
        {finalizeM.isError && (
          <div style={{ fontSize: 12, color: "tomato", textAlign: "center", marginTop: 6 }}>
            {(finalizeM.error as Error).message}
          </div>
        )}
        {offen > 0 && (
          <div style={{ fontSize: 12, color: "#a9791c", textAlign: "center", marginTop: 6 }}>
            {offen === 1 ? "1 Material ist" : `${offen} Materialien sind`} noch nicht bestätigt —
            der Lieferant lernt erst, was du bestätigst.
          </div>
        )}
        {projekteFehlen && (
          <div style={{ fontSize: 11, color: "#c2761c", textAlign: "center", marginTop: 6 }}>
            Noch nicht alle Positionen einem Projekt zugewiesen.
          </div>
        )}
      </div>

      {/* Vollbild-Ansicht. Bewusst ein Serverbild statt des PDF: ein
          eingebettetes PDF zeigt auf dem Telefon nur die erste Seite und legt
          sich in der Home-Bildschirm-App über die Bedienung — dann kam man nur
          noch durch Neustarten der App heraus. Dieselbe Entscheidung wie im
          Betrachter der privaten Dokumente. */}
      {fullscreen && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 50, display: "flex", flexDirection: "column" }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "calc(env(safe-area-inset-top) + 10px) 14px 8px", color: "#fff", flexShrink: 0 }}>
            <span style={{ fontSize: 13, fontWeight: 600, opacity: 0.85, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {beleg.lieferant || "Lieferschein"}
            </span>
            <button
              onClick={() => setFullscreen(false)}
              aria-label="Schließen"
              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, minWidth: 44, minHeight: 44, padding: "0 16px", borderRadius: 999, border: "none", background: "rgba(255,255,255,0.18)", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", flexShrink: 0 }}
            >
              <X size={20} /> Schließen
            </button>
          </div>
          {/* Tippen neben das Bild schließt ebenfalls. */}
          <div
            onClick={() => setFullscreen(false)}
            style={{ flex: 1, minHeight: 0, overflow: "auto", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "0 12px calc(env(safe-area-inset-bottom) + 16px)" }}
          >
            <img
              src={api.thumbnailUrl(id, 1400)}
              alt="Lieferschein"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "100%", height: "auto", background: "#fff", borderRadius: 8 }}
            />
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } } .spin { animation: spin 1s linear infinite; }`}</style>
    </div>
  );
}

function PositionRow({
  pos, katalog, projekte, showProjekt, belegId,
}: {
  pos: LieferscheinPosition;
  katalog: MaterialKatalog[];
  projekte: { projekt_id: string; projekt_title: string }[];
  showProjekt: boolean;
  belegId: string;
}) {
  const qc = useQueryClient();
  const [menge, setMenge] = useState(String(pos.menge ?? ""));
  const [name, setName] = useState(pos.freitext_name ?? "");
  const [einheit, setEinheit] = useState(pos.einheit ?? "");
  useEffect(() => {
    setMenge(String(pos.menge ?? ""));
    setName(pos.freitext_name ?? "");
    setEinheit(pos.einheit ?? "");
  }, [pos.id]);

  const patchM = useMutation({
    mutationFn: (patch: LieferscheinPositionPatch) => api.patchPosition(pos.id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lieferschein-positionen", belegId] }),
  });
  // Entfernen in zwei Schritten statt confirm(): der Browser-Dialog hängt in
  // der Home-Bildschirm-App gern fest.
  const [wegFragen, setWegFragen] = useState(false);
  const wegM = useMutation({
    mutationFn: () => api.deletePosition(pos.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lieferschein-positionen", belegId] }),
    onError: () => setWegFragen(false),
  });

  const lowConf = pos.ki_konfidenz > 0 && pos.ki_konfidenz < 0.6;
  const listeId = `einheiten-${pos.id}`;

  return (
    <div style={{ background: "var(--m-card)", borderRadius: 12, border: `1px solid ${lowConf || istOffen(pos) ? "#e0b050" : "var(--m-line)"}`, padding: 12 }}>
      {/* Name + Entfernen */}
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => name.trim() !== pos.freitext_name && patchM.mutate({ freitext_name: name.trim() })}
          placeholder="Bezeichnung laut Lieferschein"
          style={inp({ fontWeight: 600, flex: 1, minWidth: 0 })}
        />
        {wegFragen ? (
          <button onClick={() => wegM.mutate()} disabled={wegM.isPending}
            style={{ flex: "0 0 auto", minHeight: 44, padding: "0 12px", borderRadius: 10, border: "none", background: "#c0392b", color: "white", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            {wegM.isPending ? <Loader2 size={14} className="spin" /> : "Entfernen"}
          </button>
        ) : (
          <button onClick={() => setWegFragen(true)} aria-label="Position entfernen"
            style={{ flex: "0 0 44px", width: 44, height: 44, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 10, border: "1px solid var(--m-line)", background: "transparent", color: "var(--m-ink-3)", cursor: "pointer" }}>
            <X size={18} />
          </button>
        )}
      </div>
      {wegM.isError && (
        <div style={{ fontSize: 12, color: "tomato", marginTop: 6 }}>{(wegM.error as Error).message}</div>
      )}

      {/* Menge + Einheit */}
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <input
          value={menge}
          inputMode="decimal"
          onChange={(e) => setMenge(e.target.value)}
          onBlur={() => {
            const v = parseFloat(menge.replace(",", "."));
            if (!Number.isNaN(v) && v !== pos.menge) patchM.mutate({ menge: v });
          }}
          placeholder="Menge"
          style={inp({ flex: "0 0 96px" })}
        />
        <input
          list={listeId}
          value={einheit}
          onChange={(e) => setEinheit(e.target.value)}
          onBlur={() => einheit.trim() !== pos.einheit && patchM.mutate({ einheit: einheit.trim() })}
          placeholder="Einheit"
          style={inp({ flex: "0 0 96px" })}
        />
        <datalist id={listeId}>{EINHEITEN.map((e) => <option key={e} value={e} />)}</datalist>
        {lowConf && (
          <span style={{ alignSelf: "center", fontSize: 11, color: "#a9791c" }}>
            unsicher erkannt
          </span>
        )}
      </div>

      {/* Katalog-Verknüpfung — Autocomplete statt langer Dropdown */}
      <div style={{ marginTop: 8 }}>
        <label style={lbl()}>Material-Katalog</label>
        <MaterialPicker
          pos={pos}
          katalog={katalog}
          defaultName={name}
          defaultEinheit={einheit}
          onPick={(katalogId) => patchM.mutate({ katalog_id: katalogId })}
        />
      </div>

      {/* Projekt-Zuweisung (nur bei mehreren vorausgewählten Projekten) */}
      {showProjekt && (
        <div style={{ marginTop: 8 }}>
          <label style={lbl()}>Projekt {pos.projekt_id ? "" : "— bitte wählen"}</label>
          <select
            value={pos.projekt_id || ""}
            onChange={(e) => patchM.mutate({ projekt_id: e.target.value })}
            style={{ ...inp({ borderColor: pos.projekt_id ? "var(--m-line)" : "#e0b050" }), appearance: "auto" as const }}
          >
            <option value="">— Projekt wählen —</option>
            {projekte.map((p) => (
              <option key={p.projekt_id} value={p.projekt_id}>{p.projekt_title}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

/**
 * MaterialPicker — Autocomplete-Zuordnung gegen den Material-Katalog.
 *
 * Statt einer endlosen <select>-Dropdown: tippen → gefilterte Treffer.
 * Neue Materialien werden NICHT sofort angelegt, sondern erst in einem
 * kleinen Formular (Name + Einheit) bestätigt — so lässt sich der roh
 * erkannte Name vor der Übernahme korrigieren.
 */
function MaterialPicker({
  pos, katalog, defaultName, defaultEinheit, onPick,
}: {
  pos: LieferscheinPosition;
  katalog: MaterialKatalog[];
  defaultName: string;
  defaultEinheit: string;
  onPick: (katalogId: string) => void;
}) {
  const qc = useQueryClient();
  const matched = pos.katalog_id
    ? katalog.find((m) => m.id === pos.katalog_id) ??
      (pos.katalog_name
        ? { id: pos.katalog_id, name: pos.katalog_name, einheit: pos.einheit || "", kategorie: "" }
        : null)
    : null;

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  // Neu-Anlegen-Formular (editierbar VOR Übernahme)
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEinheit, setNewEinheit] = useState("");

  const createM = useMutation({
    mutationFn: () => api.createMaterial(newName.trim(), newEinheit.trim()),
    onSuccess: (m) => {
      qc.invalidateQueries({ queryKey: ["material-katalog"] });
      onPick(m.id);
      setCreating(false);
      setOpen(false);
      setQuery("");
    },
  });

  // Editierbares Neu-Anlegen-Formular
  if (creating) {
    const canSave = newName.trim().length > 0;
    return (
      <div style={{ padding: 10, borderRadius: 10, border: "1px solid var(--m-accent, #6db04a)", background: "rgba(109,176,74,0.06)" }}>
        <div style={{ fontSize: 11, color: "var(--m-ink-3)", marginBottom: 6, fontWeight: 600 }}>
          Neues Material — vor dem Übernehmen prüfen:
        </div>
        <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Materialbezeichnung" autoFocus style={inp({ fontWeight: 600 })} />
        <input value={newEinheit} onChange={(e) => setNewEinheit(e.target.value)} placeholder="Einheit (z.B. Stk, m, kg)" style={inp({ marginTop: 8 })} />
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <button onClick={() => setCreating(false)} disabled={createM.isPending}
            style={{ flex: "0 0 auto", padding: "9px 14px", borderRadius: 10, border: "1px solid var(--m-line)", background: "transparent", color: "var(--m-ink)", fontSize: 13, cursor: "pointer" }}>
            Abbrechen
          </button>
          <button onClick={() => canSave && createM.mutate()} disabled={createM.isPending || !canSave}
            style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "9px 14px", borderRadius: 10, border: "none", background: "var(--m-accent, #6db04a)", color: "white", fontSize: 13, fontWeight: 700, cursor: createM.isPending || !canSave ? "not-allowed" : "pointer", opacity: createM.isPending || !canSave ? 0.6 : 1 }}>
            {createM.isPending ? <Loader2 size={14} className="spin" /> : <Check size={14} />} Übernehmen
          </button>
        </div>
        {createM.isError && <div style={{ fontSize: 12, color: "tomato", marginTop: 8 }}>{(createM.error as Error).message}</div>}
      </div>
    );
  }

  // Keine Ware (Fracht, Pfand, Maut): zählt nicht als Material.
  if ((pos.zuordnung ?? "") === "ignoriert" && !open) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: "var(--m-ink-3)", border: "1px solid var(--m-line)", padding: "3px 8px", borderRadius: 999 }}>
          keine Ware
        </span>
        <span style={{ fontSize: 13, color: "var(--m-ink-3)" }}>zählt nicht als geliefertes Material</span>
        <button onClick={() => { setOpen(true); setQuery(""); }} style={linkBtn()}>doch Material</button>
      </div>
    );
  }

  // Bereits zugeordnet → kompakte Anzeige + „ändern". Ein Vorschlag (unsicherer
  // Namensabgleich) ist gelb markiert und braucht ein „Stimmt“ — erst dann
  // merkt sich der Lieferant die Zuordnung.
  if (matched && !open) {
    const vorschlag = OFFEN.has(pos.zuordnung ?? "");
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 700, color: "white", background: vorschlag ? "#c2761c" : "var(--m-accent, #6db04a)", padding: "3px 8px", borderRadius: 999 }}>
          {vorschlag ? "Vorschlag" : <><Check size={12} /> zugeordnet</>}
        </span>
        <strong style={{ fontSize: 14, color: "var(--m-ink)" }}>
          {matched.name}{matched.einheit ? ` (${matched.einheit})` : ""}
        </strong>
        {vorschlag && (
          <button onClick={() => onPick(matched.id)}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, minHeight: 44, padding: "0 14px", borderRadius: 10, border: "none", background: "var(--m-accent, #6db04a)", color: "white", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            <Check size={14} /> Stimmt
          </button>
        )}
        <button onClick={() => { setOpen(true); setQuery(""); }} style={linkBtn()}>
          {vorschlag ? "anderes Material" : "ändern"}
        </button>
      </div>
    );
  }

  const q = query.trim().toLowerCase();
  const filtered = (q ? katalog.filter((m) => m.name.toLowerCase().includes(q)) : katalog).slice(0, 8);
  const exactHit = !!q && katalog.some((m) => m.name.trim().toLowerCase() === q);
  const vorschlagName = (query.trim() || defaultName.trim() || pos.freitext_name || "").trim();

  return (
    <div>
      <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
        <Search size={15} style={{ position: "absolute", left: 10, color: "var(--m-ink-3)" }} />
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Material tippen zum Suchen…"
          style={inp({ paddingLeft: 32 })}
        />
      </div>
      {open && (
        <div style={{ marginTop: 6, borderRadius: 10, border: "1px solid var(--m-line)", background: "var(--m-card)", overflow: "hidden" }}>
          <div style={{ maxHeight: 200, overflowY: "auto" }}>
            {filtered.map((m) => (
              <button key={m.id}
                onClick={() => { onPick(m.id); setOpen(false); setQuery(""); }}
                style={{ width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 6, padding: "9px 12px", background: "transparent", border: "none", color: "var(--m-ink)", fontSize: 14, cursor: "pointer" }}>
                {m.name}{m.einheit ? <span style={{ color: "var(--m-ink-3)", fontSize: 12 }}>({m.einheit})</span> : null}
              </button>
            ))}
            {filtered.length === 0 && (
              <div style={{ padding: "9px 12px", color: "var(--m-ink-3)", fontSize: 13 }}>Kein Treffer im Katalog.</div>
            )}
          </div>
          {!exactHit && vorschlagName && (
            <button
              onClick={() => { setNewName(vorschlagName); setNewEinheit(defaultEinheit.trim() || pos.einheit || ""); setCreating(true); }}
              style={{ width: "100%", display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 12px", border: "none", borderTop: "1px solid var(--m-line)", background: "rgba(109,176,74,0.06)", color: "var(--m-accent, #6db04a)", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              <Plus size={14} /> «{vorschlagName}» neu anlegen
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Position von Hand ergänzen — wenn die Erkennung eine Zeile übersehen hat. */
function PositionNeu({ belegId }: { belegId: string }) {
  const qc = useQueryClient();
  const [offen, setOffen] = useState(false);
  const [name, setName] = useState("");
  const [menge, setMenge] = useState("");
  const [einheit, setEinheit] = useState("");
  const neuM = useMutation({
    mutationFn: () => {
      const m = parseFloat(menge.replace(",", "."));
      return api.addPosition(belegId, {
        freitext_name: name.trim(),
        menge: Number.isNaN(m) ? 0 : m,
        einheit: einheit.trim(),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lieferschein-positionen", belegId] });
      setName(""); setMenge(""); setEinheit(""); setOffen(false);
    },
  });

  if (!offen) {
    return (
      <button onClick={() => setOffen(true)}
        style={{ width: "100%", minHeight: 44, marginTop: 10, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, borderRadius: 12, border: "1px dashed var(--m-line)", background: "transparent", color: "var(--m-ink-2)", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
        <Plus size={16} /> Position hinzufügen
      </button>
    );
  }
  const kannSpeichern = name.trim().length > 0 && !neuM.isPending;
  return (
    <div style={{ marginTop: 10, background: "var(--m-card)", borderRadius: 12, border: "1px solid var(--m-accent, #6db04a)", padding: 12 }}>
      <input value={name} onChange={(e) => setName(e.target.value)} autoFocus
        placeholder="Bezeichnung laut Lieferschein" style={inp({ fontWeight: 600 })} />
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <input value={menge} inputMode="decimal" onChange={(e) => setMenge(e.target.value)}
          placeholder="Menge" style={inp({ flex: "0 0 96px" })} />
        <input list="einheiten-neu" value={einheit} onChange={(e) => setEinheit(e.target.value)}
          placeholder="Einheit" style={inp({ flex: "0 0 96px" })} />
        <datalist id="einheiten-neu">{EINHEITEN.map((e) => <option key={e} value={e} />)}</datalist>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button onClick={() => setOffen(false)} disabled={neuM.isPending}
          style={{ flex: "0 0 auto", minHeight: 44, padding: "0 14px", borderRadius: 10, border: "1px solid var(--m-line)", background: "transparent", color: "var(--m-ink)", fontSize: 14, cursor: "pointer" }}>
          Abbrechen
        </button>
        <button onClick={() => kannSpeichern && neuM.mutate()} disabled={!kannSpeichern}
          style={{ flex: 1, minHeight: 44, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, borderRadius: 10, border: "none", background: "var(--m-accent, #6db04a)", color: "white", fontSize: 14, fontWeight: 700, cursor: kannSpeichern ? "pointer" : "not-allowed", opacity: kannSpeichern ? 1 : 0.6 }}>
          {neuM.isPending ? <Loader2 size={14} className="spin" /> : <Plus size={14} />} Hinzufügen
        </button>
      </div>
      {neuM.isError && <div style={{ fontSize: 12, color: "tomato", marginTop: 8 }}>{(neuM.error as Error).message}</div>}
      <div style={{ fontSize: 11, color: "var(--m-ink-3)", marginTop: 8 }}>
        Das Material wählst du danach an der neuen Position.
      </div>
    </div>
  );
}

function linkBtn(): React.CSSProperties {
  return { background: "transparent", border: "none", color: "var(--m-ink-3)", fontSize: 13, textDecoration: "underline", cursor: "pointer", minHeight: 44, padding: "0 4px" };
}

function Loading() {
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
      <Loader2 size={22} className="spin" style={{ color: "var(--m-ink-3)" }} />
    </div>
  );
}

function inp(extra: React.CSSProperties = {}): React.CSSProperties {
  return {
    width: "100%",
    height: 44,
    borderRadius: 10,
    border: "1px solid var(--m-line)",
    background: "var(--m-bg)",
    color: "var(--m-ink)",
    padding: "0 12px",
    // 16 px: darunter zoomt iOS beim Antippen hinein und kommt nicht zurück.
    fontSize: 16,
    boxSizing: "border-box",
    ...extra,
  };
}

function lbl(): React.CSSProperties {
  return { display: "block", fontSize: 11, color: "var(--m-ink-3)", marginBottom: 4, fontWeight: 600 };
}
